import { WbMoreOptions, WbUnstyledLink } from '@agilelab/plugin-wb-platform';
import {
  PrototypeStatus,
  PrototypeStatusBadge,
  SystemPrototype,
} from '@agilelab/plugin-wb-system-prototype-common';
import { useRouteRef } from '@mainstage/core-plugin-api';
import {
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import clsx from 'clsx';
import { FC, useState } from 'react';
import { systemPrototypeDetailRouteRef } from '../../../../routes';

type PrototypeActionsColumnProps = {
  prototype: SystemPrototype;
  onChangeStatus: (
    prototype: SystemPrototype,
    newStatus: PrototypeStatus,
  ) => void;
  onDelete: (prototype: SystemPrototype) => void;
};

const useStyles = makeStyles(
  theme => ({
    statusEditElement: {
      minWidth: 200,
      padding: theme.spacing(1),
    },
    statusEditElementSelected: {
      backgroundColor: theme.palette.softened.primary,
    },
  }),
  { name: 'PrototypeActionsTable' },
);

export const PrototypeActionsColumn: FC<PrototypeActionsColumnProps> = ({
  prototype,
  onChangeStatus,
  onDelete,
}) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const onMenuClose = () => setMenuOpen(false);

  const getDetailPageUrl = useRouteRef(systemPrototypeDetailRouteRef);

  return (
    <WbMoreOptions
      open={menuOpen}
      MenuProps={{
        onClick: e => e.stopPropagation(),
      }}
      onClick={e => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
      onClose={onMenuClose}
    >
      <MenuItem
        component={WbUnstyledLink}
        to={getDetailPageUrl({ id: prototype.id.toString() ?? '.' })}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        Edit
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={e => {
          e.stopPropagation();
          setStatusMenuAnchorEl(e.currentTarget);
        }}
      >
        <ListItemIcon>
          <SwapHorizIcon fontSize="small" />
        </ListItemIcon>
        Change Status
        <ListItemIcon style={{ marginLeft: 20 }}>
          <ChevronRightIcon fontSize="small" />
        </ListItemIcon>
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={e => {
          e.stopPropagation();
          onDelete(prototype);
          onMenuClose();
        }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        Delete
      </MenuItem>
      {/* Status submenu */}
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={() => setStatusMenuAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        getContentAnchorEl={null}
      >
        {Object.values(PrototypeStatus).map(statusKey => (
          <MenuItem
            disabled={prototype.status === statusKey}
            key={statusKey}
            className={clsx(
              classes.statusEditElement,
              prototype.status === statusKey &&
                classes.statusEditElementSelected,
            )}
            onClick={
              prototype.status !== statusKey
                ? () => {
                    onChangeStatus(prototype, statusKey);
                    setStatusMenuAnchorEl(null);
                    onMenuClose();
                  }
                : undefined
            }
          >
            <PrototypeStatusBadge status={statusKey} />
          </MenuItem>
        ))}
      </Menu>
    </WbMoreOptions>
  );
};
